import { sortKidsGroups } from '../components/kids/kidsUi';
import { publicOrigin } from '../config';

async function call(action, params = {}) {
  const qs = new URLSearchParams({ action });
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    qs.set(k, typeof v === 'object' ? JSON.stringify(v) : v);
  });

  const response = await fetch(`/api/proxy?${qs}`);
  const data = await response.json();
  if (!data.ok) throw new Error(data.error || 'API error');
  return data.data;
}

// POST variant for large payloads (CSV imports) that would overflow a URL.
async function callPost(action, params = {}) {
  const response = await fetch('/api/proxy', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...params }),
  });
  const data = await response.json();
  if (!data.ok) throw new Error(data.error || 'API error');
  return data.data;
}

// ── Session-level cache for shared read-mostly data ───────
// Keyed by action name. Cleared by invalidateCache(key).
// TTL: 5 minutes — stale reads are fine for schedule/people within a session.
const _cache = {};
const CACHE_TTL = 5 * 60 * 1000;

function callCached(cacheKey, action, params = {}) {
  const entry = _cache[cacheKey];
  if (entry && Date.now() - entry.ts < CACHE_TTL) return Promise.resolve(entry.data);
  return call(action, params).then(data => {
    _cache[cacheKey] = { data, ts: Date.now() };
    return data;
  });
}

export function invalidateCache(key) {
  if (key) delete _cache[key];
  else Object.keys(_cache).forEach(k => delete _cache[k]);
}

// Auth
export const getUser = (email) => call('getUser', { email });

// People — cached: the full people list changes rarely within a session
export const getPeople = () => callCached('people', 'getPeople');
export const getPerson = (id) => call('getPerson', { id });
export const upsertPerson = (person) => {
  invalidateCache('people');
  return call('upsertPerson', { person });
};
export const deletePerson = (id) => {
  invalidateCache('people');
  return call('deletePerson', { id });
};

// Teams — cached
export const getTeams = () => callCached('teams', 'getTeams');
export const getTeam = (id) => call('getTeam', { id });
export const upsertTeam = (team) => { invalidateCache('teams'); return call('upsertTeam', { team }); };
export const deleteTeam = (id) => { invalidateCache('teams'); return call('deleteTeam', { id }); };

// Team Lead Access
export const getTeamLeads = () => call('getTeamLeads');
export const upsertTeamLead = (lead) => call('upsertTeamLead', { lead });
export const deleteTeamLead = (email) => call('deleteTeamLead', { email });

// Team Members — writes bust the team's cached mega-endpoint data so a
// team-switch round trip can't resurrect the pre-edit roster.
export const getTeamMembers = (teamId) => call('getTeamMembers', { teamId });
export const getAllTeamMemberships = () => call('getAllTeamMemberships');
export const addTeamMember = (teamId, personId, role) => {
  invalidateCache(`teamData-${teamId}`);
  return call('addTeamMember', { teamId, personId, role });
};
export const removeTeamMember = (teamId, personId) => {
  invalidateCache(`teamData-${teamId}`);
  return call('removeTeamMember', { teamId, personId });
};
export const syncPassLevelsFromTeams = () => call('syncPassLevelsFromTeams');

// Brushfire — manual "Sync Now": pulls recently-changed servant leaders immediately
export const syncBrushfireNow = () => call('syncBrushfireNow');
// Full sweep: reconciles every SL's current teams (catches team moves that
// don't bump Brushfire's Last Modified). Falls back to the incremental sync
// when the backend hasn't been redeployed with the deep endpoint yet.
export const syncBrushfireDeep = () =>
  call('syncBrushfireDeep').catch(() => call('syncBrushfireNow'));
// Merge one team into another (rosters, blocks, positions move; source removed)
export const mergeTeams = (sourceId, targetId) => {
  invalidateCache();
  return call('mergeTeams', { sourceId, targetId });
};

// Merge a duplicate person into the kept row — every reference (positions,
// rosters, flags, availability) moves to the keeper before the dupe is removed.
export const mergePeople = (keepId, mergeId) => {
  invalidateCache();
  return call('mergePeople', { keepId, mergeId });
};

// Background checks — CSV import (POST: ~2k rows won't fit in a URL)
export const importBackgroundChecks = (rows) => {
  invalidateCache('people');
  return callPost('importBackgroundChecks', { rows });
};

// Master Schedule — cached: changes rarely during a session
export const getSchedule = (teamId) => callCached('schedule', 'getSchedule', { teamId });
export const upsertScheduleItem = (item) => {
  invalidateCache('schedule');
  return call('upsertScheduleItem', { item });
};
export const deleteScheduleItem = (id) => {
  invalidateCache('schedule');
  return call('deleteScheduleItem', { id });
};
// Pull the master schedule from the configured "bible" spreadsheet into the app
export const syncMasterSchedule = () => {
  invalidateCache('schedule');
  return call('syncMasterScheduleNow');
};

// Team-created schedule items (durable, separate sheet). These come back as part
// of getTeamData, so writes invalidate that team's cached data.
export const getTeamScheduleItems = (teamId) => call('getTeamScheduleItems', { teamId });
export const upsertTeamScheduleItem = (item) => {
  invalidateCache(`teamData-${item && item.teamId}`);
  return call('upsertTeamScheduleItem', { item });
};
export const deleteTeamScheduleItem = (id, teamId) => {
  if (teamId) invalidateCache(`teamData-${teamId}`);
  return call('deleteTeamScheduleItem', { id });
};

// Blocks — writes bust the cached team data (blocks/assignments live there).
// Deletes only get an id, so they clear the whole cache — over-invalidation is
// just a refetch, stale data is a lost edit.
export const getBlocks = (teamId, subRole) => call('getBlocks', { teamId, subRole });
export const upsertBlock = (block) => {
  if (block && block.teamId) invalidateCache(`teamData-${block.teamId}`);
  else invalidateCache();
  return call('upsertBlock', { block });
};
export const deleteBlock = (id) => {
  invalidateCache();
  return call('deleteBlock', { id });
};

// Assignments (Block Positions)
export const getAssignments = (blockId) => call('getAssignments', { blockId });
export const getPersonAssignments = (personId) => call('getPersonAssignments', { personId });
export const upsertAssignment = (assignment) => {
  if (assignment && assignment.teamId) invalidateCache(`teamData-${assignment.teamId}`);
  else invalidateCache();
  return call('upsertAssignment', { assignment });
};
export const deleteAssignment = (id) => {
  invalidateCache();
  return call('deleteAssignment', { id });
};
// Persist manual position order within a block (drag-to-reorder)
export const reorderAssignments = (blockId, orderedIds) => call('reorderAssignments', { blockId, orderedIds });
// Persist manual block order for a team (drag-to-reorder)
export const reorderBlocks = (teamId, orderedIds) => {
  invalidateCache(`teamData-${teamId}`);
  return call('reorderBlocks', { teamId, orderedIds });
};
export const checkConflict = (personId, blockId) => call('checkConflict', { personId, blockId });

// Block Template (single — legacy, still used by AddBlockModal fallback)
export const getBlockTemplate = (teamId, subRole) => call('getBlockTemplate', { teamId, subRole });
export const saveBlockTemplate = (teamId, positions, subRole) => call('saveBlockTemplate', { teamId, positions, subRole });
// Block Templates (multiple named) — cached per teamId+subRole
export const getBlockTemplates = (teamId, subRole) =>
  callCached(`blockTemplates-${teamId}-${subRole || ''}`, 'getBlockTemplates', { teamId, subRole });
export const saveBlockTemplates = (teamId, templates, subRole) => {
  invalidateCache(`blockTemplates-${teamId}-${subRole || ''}`);
  return call('saveBlockTemplates', { teamId, templates, subRole });
};

// Team Data — mega-endpoint, cached per team. Invalidate when you need a guaranteed-fresh reload.
export const getTeamData = (teamId) => callCached(`teamData-${teamId}`, 'getTeamData', { teamId });
export const invalidateTeamData = (teamId) => invalidateCache(`teamData-${teamId}`);

// Team Manual — saved to Settings sheet, returned by getTeamData
export const saveTeamManual = (teamId, url, label) => {
  invalidateCache(`teamData-${teamId}`);
  return call('saveTeamManual', { teamId, url, label });
};

// Pass Levels — cached globally
export const getPassLevels = () => callCached('passLevels', 'getPassLevels');
export const savePassLevels = (levels) => {
  invalidateCache('passLevels');
  return call('savePassLevels', { levels });
};

// Team Roles — cached per team
export const getTeamRoles = (teamId) => callCached(`teamRoles-${teamId}`, 'getTeamRoles', { teamId });
export const saveTeamRoles = (teamId, enabled, roles) => {
  invalidateCache(`teamRoles-${teamId}`);
  invalidateCache(`teamData-${teamId}`);
  return call('saveTeamRoles', { teamId, enabled, roles });
};
export const getPersonRoles = (teamId) => call('getPersonRoles', { teamId });
export const setPersonRole = (teamId, personId, role) => {
  invalidateCache(`teamData-${teamId}`);
  return call('setPersonRole', { teamId, personId, role });
};
export const setPersonRoles = (teamId, personId, roles) => {
  invalidateCache(`teamData-${teamId}`);
  return call('setPersonRoles', { teamId, personId, roles });
};

// Availability
export const getAvailability = (personId, teamId) => call('getAvailability', { personId, teamId });
export const setAvailability = (availability) => call('setAvailability', { availability });

// Kids availability ("Most helpful 3") — Kids-only flow
export const getKidsAvailabilityRequest = (personId) => call('getKidsAvailabilityRequest', { personId });
export const setKidsAvailability = (payload) => call('setKidsAvailability', { payload });
export const getKidsAvailability = (teamId) => call('getKidsAvailability', { teamId });


// Serving availability (general SL "preferred serving times" — day × slot grid)
export const getServingAvailabilityRequest = (personId) => call('getServingAvailabilityRequest', { personId });
export const setServingAvailability = (payload) => call('setServingAvailability', { payload });
export const getServingAvailability = (teamId) => call('getServingAvailability', { teamId });

// Kids cross-department reads (Phase 3)
export const getKidsProgramBlocks = () => call('getKidsProgramBlocks');
export const getKidsMealCounts = () => call('getKidsMealCounts');

// Kids rotations / Color Wheel (Phase 5)
export const getKidsRotations = (program) => call('getKidsRotations', { program });
export const saveKidsRotations = (program, data) => call('saveKidsRotations', { program, data });

// Kids V2 — children, small groups, allergies, availability, details.
// Groups always come back in age order (3s & 4s → 5s & 6s → Elementary by
// grade) so every dropdown/card list reads naturally.
export const getKidsChildren = () =>
  call('getKidsChildren').then(d => (d && d.groups ? { ...d, groups: sortKidsGroups(d.groups) } : d));
export const upsertKidsChild = (child) => call('upsertKidsChild', { child });
export const deleteKidsChild = (id) => call('deleteKidsChild', { id });
export const getKidsGroups = () => call('getKidsGroups').then(g => sortKidsGroups(g || []));
export const upsertKidsGroup = (group) => call('upsertKidsGroup', { group });
export const deleteKidsGroup = (id) => call('deleteKidsGroup', { id });
export const syncKidsChildrenNow = () => call('syncKidsChildrenNow');
export const installKidsChildSyncTrigger = () => call('installKidsChildSyncTrigger');
export const removeKidsChildSyncTrigger = () => call('removeKidsChildSyncTrigger');
export const getKidsPublicForm = () => call('getKidsPublicForm');
export const submitKidsPublicAvailability = (payload) => call('submitKidsPublicAvailability', { payload });
export const sendKidsAvailabilityRequests = (senderEmail) =>
  call('sendKidsAvailabilityRequests', { senderEmail, baseUrl: window.location.origin });
export const sendKidsAvailabilityTest = (senderEmail) =>
  call('sendKidsAvailabilityTest', { senderEmail, baseUrl: window.location.origin });
export const getKidsDailyDetails = (blockId) => call('getKidsDailyDetails', { blockId });
export const saveKidsDailyDetails = (blockId, data) => call('saveKidsDailyDetails', { blockId, data });
export const getKidsDetailSheets = () => call('getKidsDetailSheets');
export const saveKidsDetailSheets = (list) => call('saveKidsDetailSheets', { list });
export const getKidsSGLMap = () => call('getKidsSGLMap');
export const setKidsSGL = (personId, groupId) => call('setKidsSGL', { personId, groupId });


// Meals
export const getMeals = () => call('getMeals');
export const upsertMeal = (meal) => call('upsertMeal', { meal });
export const getMealCounts = () => call('getMealCounts');
export const getTeamMealTimes = (teamId) => call('getTeamMealTimes', { teamId });
export const setTeamMealTime = (mealTime) => {
  if (mealTime && mealTime.teamId) invalidateCache(`teamData-${mealTime.teamId}`);
  return call('setTeamMealTime', { mealTime });
};
export const getMealWindows = () => call('getMealWindows');
export const setMealWindow = (mealWindow) => call('setMealWindow', { mealWindow });

// Person Schedule (public — no auth needed, uses personId token)
export const getPersonSchedule = (personId, fresh) => call('getPersonSchedule', fresh ? { personId, fresh: 1 } : { personId });
export const getDemoData = () => call('getDemoData');
export const respondToAssignment = (assignmentId, personId, status, reason) =>
  call('respondToAssignment', {
    assignmentId, personId, status, reason,
    // So the accept-confirmation email links back to wherever the app is served.
    baseUrl: (typeof window !== 'undefined' && window.location && window.location.origin) || '',
  });

// Team Documents (multi-doc)
export const getTeamDocs = (teamId) => call('getTeamDocs', { teamId });
export const saveTeamDocs = (teamId, docs) => {
  invalidateCache(`teamData-${teamId}`);
  return call('saveTeamDocs', { teamId, docs });
};

// Radio Flags
export const getRadioFlags = (teamId) => call('getRadioFlags', { teamId });
export const setRadioFlag = (teamId, personId, flagged) => {
  invalidateCache(`teamData-${teamId}`);
  return call('setRadioFlag', { teamId, personId, flagged });
};
export const getRadioCount = () => call('getRadioCount');
// Count of unique people who have accepted ≥1 assignment invite (Overview "Rostered")
export const getAcceptedCount = () => call('getAcceptedCount');
// Every unsent assignment across all teams — admin-wide Send Requests
export const getAllUnsent = () => call('getAllUnsent');

// General Team Email — POST, not GET: a whole-roster recipient list plus the
// message body overflows a querystring and the proxy rejects the URL.
export const sendTeamEmail = (teamId, to, subject, body, senderEmail) =>
  callPost('sendTeamEmail', { teamId, to, subject, body, senderEmail });

// Check-In
export const getCheckInData = (query, personId) => call('getCheckInData', personId ? { personId } : { query });
export const searchCheckInNames = (query) => call('searchCheckInNames', { query });
export const getAllCheckInCards = () => call('getAllCheckInCards');
export const getCheckInStats = (refresh) => call('getCheckInStats', refresh ? { refresh: 1 } : {});
export const checkInPerson = (personId) => call('checkInPerson', { personId });
export const uncheckInPerson = (personId) => call('uncheckInPerson', { personId });
export const getCheckIns = () => call('getCheckIns');

// Schedule Change Notification
export const sendScheduleChangeEmail = (to, name, personId, senderEmail) =>
  call('sendScheduleChangeEmail', { to, name, personId, senderEmail, baseUrl: appBaseUrl() });

// Daily Scheduled Emails
export const getScriptOwnerEmail = () => call('getScriptOwnerEmail');
export const getDailyEmailSettings = () => call('getDailyEmailSettings');
export const saveDailyEmailSettings = (settings) => call('saveDailyEmailSettings', { settings });
export const setupDailyEmailTrigger = (settings) => call('setupDailyEmailTrigger', { settings });
export const removeDailyEmailTrigger = () => call('removeDailyEmailTrigger');

// Settings + Email
export const getSettings = () => call('getSettings');
export const upsertSetting = (key, value) => call('upsertSetting', { key, value });
// baseUrl tells the backend what host to build schedule links from. Always the
// canonical custom domain (localhost excepted for dev) so emailed links never
// carry the vercel.app host the admin happened to be browsing on.
const appBaseUrl = () => publicOrigin();
export const sendScheduleLink = (to, name, personId, templateId, senderEmail) =>
  call('sendScheduleLink', { to, name, personId, templateId, senderEmail, baseUrl: appBaseUrl() });
// "When can you serve?" availability request — links to /serve/:personId
export const sendAvailabilityRequest = (to, name, personId, senderEmail) =>
  call('sendAvailabilityRequest', { to, name, personId, senderEmail, baseUrl: appBaseUrl() });
export const sendAssignmentEmail = (to, name, personId, blockName, teamName, senderEmail, templateId) =>
  call('sendAssignmentEmail', { to, name, personId, blockName, teamName, senderEmail, templateId, baseUrl: appBaseUrl() });
